import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
 Camera, Plus, Calendar, Fingerprint, Loader2, Trash2, ShieldCheck,
 Copy, LogOut
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import EmptyState from "@/components/EmptyState";

/**
 * PhotoTrailTab
 *
 * Tenant-side monthly check-in log. Renters take photos of each room/area
 * once a month. each photo gets a SHA-256 hash and server timestamp.
 * When a bad agent fabricates an exit report, the tenant has their own
 * dated, hash-verified parallel record to demolish the lies in tribunal.
 *
 * Flow:
 * 1. Pick a room category from the dropdown
 * 2. Drop photos in the upload zone
 * 3. Add an optional note ("kitchen tap leaking", "garden mown last week")
 * 4. Submit. backend uploads photos as Documents + creates a PhotoTrailEntry
 * grouping them
 * 5. Timeline below shows every check-in chronologically with thumbnails
 * and hash verification links.
 */
export default function PhotoTrailTab({ caseId, onSwitchTab }) {
 const { user } = useAuth();
 const [rooms, setRooms] = useState([]);
 const [entries, setEntries] = useState([]);
 const [loading, setLoading] = useState(true);
 const [showForm, setShowForm] = useState(false);
 const [roomCategory, setRoomCategory] = useState("Living room");
 const [notes, setNotes] = useState("");
 const [isExit, setIsExit] = useState(false);
 const [files, setFiles] = useState([]);
 const [busy, setBusy] = useState(false);
 const fileInputRef = useRef(null);

 const isFree = user?.tier === "free";

 // Load rooms list + entries
 useEffect(() => {
 let alive = true;
 Promise.all([
 api.get("/photo-trail/rooms").then(r => r.data.rooms),
 api.get(`/cases/${caseId}/photo-trail`).then(r => r.data),
 ])
.then(([roomList, entryList]) => {
 if (!alive) return;
 setRooms(roomList);
 setEntries(entryList);
 })
.catch(e => alive && toast.error(e.response?.data?.detail || "Couldn't load Photo Trail"))
.finally(() => alive && setLoading(false));
 return () => { alive = false; };
 }, [caseId]);

 const reload = async () => {
 const r = await api.get(`/cases/${caseId}/photo-trail`);
 setEntries(r.data);
 };

 const submit = async (e) => {
 e?.preventDefault();
 if (!files.length) return toast.error("Pick at least one photo first.");
 setBusy(true);
 try {
 // 1. Upload each photo via bulk endpoint
 const form = new FormData();
 files.forEach(f => form.append("files", f));
 const upRes = await api.post(`/cases/${caseId}/documents/bulk`, form, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 const created = upRes.data.created || [];
 const skipped = upRes.data.skipped || [];
 if (!created.length) {
 toast.error(skipped[0]?.reason || "All uploads were rejected.");
 return;
 }
 if (skipped.length) {
 toast.warning(`${skipped.length} file(s) skipped. ${skipped[0].reason}`);
 }
 // 2. Group them into a PhotoTrailEntry
 await api.post(`/cases/${caseId}/photo-trail`, {
 room_category: roomCategory,
 document_ids: created.map(d => d.id),
 notes: notes || null,
 is_exit: isExit,
 });
 toast.success(`Saved. ${created.length} photo(s) safely dated and locked in.`);
 setShowForm(false);
 setFiles([]);
 setNotes("");
 setIsExit(false);
 await reload();
 } catch (err) {
 const code = err.response?.status;
 const msg = err.response?.data?.detail;
 if (code === 402) {
 toast.error(msg || "Photo Trail is a paid feature. Upgrade to Vault, Compass or Pro.");
 } else {
 toast.error(msg || "Couldn't save check-in. Try again.");
 }
 } finally {
 setBusy(false);
 }
 };

 const deleteEntry = async (entryId) => {
 if (!confirm("Delete this check-in? Photos stay in your documents. only the check-in record is removed.")) return;
 try {
 await api.delete(`/photo-trail/entries/${entryId}`);
 toast.success("Check-in deleted.");
 await reload();
 } catch (e) {
 toast.error(e.response?.data?.detail || "Couldn't delete");
 }
 };

 const copyHash = (hash) => {
 navigator.clipboard.writeText(hash);
 toast.success("Verification code copied");
 };

 const verifyUrl = (hash) =>
 `${window.location.origin}/verify/${hash}`;

 if (loading) {
 return (
 <div className="space-y-4" data-testid="photo-trail-loading">
 <div className="skeleton h-24 w-full" />
 <div className="skeleton h-32 w-full" />
 <div className="skeleton h-32 w-full" />
 </div>
 );
 }

 return (
 <div data-testid="photo-trail-tab">
 {/* Header card explaining the feature */}
 <div className="card mb-6 border-l-4 border-l-[#1B6C62]">
 <div className="flex flex-wrap items-start justify-between gap-4">
 <div className="flex-1 min-w-[260px]">
 <div className="flex items-center gap-2 mb-1">
 <Camera size={18} strokeWidth={1.5} className="text-[#1B6C62]" />
 <h3 className="font-serif text-xl text-[#0D2B52]">Photo Trail</h3>
 </div>
 <p className="text-[13px] text-[#5a6b7a] leading-relaxed max-w-2xl">
 A friendly monthly photo log of your home. Just snap a few rooms,
 we date and lock them in safely. If anyone tries to twist the
 story later about how you looked after the place, you&rsquo;ve
 already got the truth on file.
 </p>
 <p className="text-[12px] text-[#1B6C62] mt-2 font-medium">
 We&rsquo;ll send a friendly nudge once a month. Skip a month if everything&rsquo;s fine.
 </p>
 </div>
 {!isFree && (
 <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
 <button
 onClick={() => setShowForm(true)}
 className="btn-teal inline-flex items-center gap-1.5 text-sm justify-center"
 data-testid="photo-trail-new-checkin-btn"
 >
 <Plus size={14} strokeWidth={1.75} /> New check-in
 </button>
 {onSwitchTab && (
 <button
 onClick={() => onSwitchTab("compare")}
 className="text-xs text-[#1B6C62] hover:underline inline-flex items-center gap-1 justify-center"
 data-testid="photo-trail-compare-link"
 title="Place a Photo Trail entry next to an agent's photo and have Compass analyse the difference"
 >
 Compare against agent&apos;s photo →
 </button>
 )}
 </div>
 )}
 </div>
 </div>

 {/* Upgrade nudge for free users */}
 {isFree && (
 <div className="card mb-6 border-l-4 border-l-[#C9A646]" data-testid="photo-trail-upgrade-nudge">
 <h4 className="font-serif text-lg text-[#0D2B52] mb-1">Start protecting your bond, upgrade to unlock</h4>
 <p className="text-sm text-[#5a6b7a] mb-3">
 Photo Trail is on every paid plan (Vault, Compass, Pro). One photo
 session a month is all it takes to build a quiet record that
 protects your bond and your renting reputation.
 </p>
 <a href="/pricing" className="btn-teal inline-flex items-center gap-1.5 text-sm" data-testid="photo-trail-upgrade-link">
 See plans
 </a>
 </div>
 )}

 {/* New-entry form */}
 {showForm && !isFree && (
 <form onSubmit={submit} className="card mb-6 border-l-4 border-l-[#0D2B52]" data-testid="photo-trail-form">
 <h4 className="font-serif text-lg text-[#0D2B52] mb-3">New check-in</h4>
 <div className="grid sm:grid-cols-2 gap-4 mb-4">
 <div>
 <label className="label-mono text-[10px] text-[#5a6b7a] block mb-1">Room / area</label>
 <select
 value={roomCategory}
 onChange={(e) => setRoomCategory(e.target.value)}
 className="w-full border border-[#E5E4DE] bg-white px-3 py-2 text-sm font-serif"
 data-testid="photo-trail-room-select"
 >
 {rooms.map(r => <option key={r} value={r}>{r}</option>)}
 </select>
 </div>
 <div className="flex items-end gap-2 pb-1">
 <label className="inline-flex items-center gap-2 text-sm text-[#1A1A1A] cursor-pointer">
 <input
 type="checkbox"
 checked={isExit}
 onChange={(e) => setIsExit(e.target.checked)}
 data-testid="photo-trail-is-exit-checkbox"
 />
 <LogOut size={13} strokeWidth={1.5} />
 Mark as exit (move-out) check-in
 </label>
 </div>
 </div>
 <label className="label-mono text-[10px] text-[#5a6b7a] block mb-1">Notes (optional)</label>
 <textarea
 value={notes}
 onChange={(e) => setNotes(e.target.value)}
 placeholder="e.g. Kitchen tap still leaking from last week. Garden mown yesterday."
 rows={2}
 className="w-full border border-[#E5E4DE] bg-white px-3 py-2 text-sm font-serif mb-4"
 data-testid="photo-trail-notes"
 />
 <label className="label-mono text-[10px] text-[#5a6b7a] block mb-1">Photos</label>
 <div
 onClick={() => fileInputRef.current?.click()}
 className="border-2 border-dashed border-[#E5E4DE] hover:border-[#1B6C62] transition-colors p-6 text-center cursor-pointer mb-4"
 data-testid="photo-trail-dropzone"
 >
 <Camera size={28} strokeWidth={1.25} className="text-[#5a6b7a] mx-auto mb-2" />
 <p className="text-sm text-[#1A1A1A]">
 {files.length ? `${files.length} photo(s) selected. tap to add more` : "Tap to pick photos (drag & drop works too)"}
 </p>
 <p className="text-[11px] text-[#5a6b7a] mt-1">
 We'll auto-compress and preserve EXIF. iPhone or Android photos both welcome.
 </p>
 </div>
 <input
 ref={fileInputRef}
 type="file"
 accept="image/*"
 multiple
 className="hidden"
 onChange={(e) => setFiles(Array.from(e.target.files || []))}
 data-testid="photo-trail-file-input"
 />
 {files.length > 0 && (
 <div className="flex flex-wrap gap-2 mb-4">
 {files.map((f, i) => (
 <span key={i} className="px-2 py-1 bg-[#EFEDE5] text-[11px] font-mono text-[#1A1A1A]">{f.name}</span>
 ))}
 </div>
 )}
 <div className="flex gap-2">
 <button type="submit" disabled={busy || !files.length} className="btn-teal inline-flex items-center gap-1.5 text-sm disabled:opacity-60" data-testid="photo-trail-submit-btn">
 {busy ? <><Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> Saving…</> : "Save check-in"}
 </button>
 <button type="button" onClick={() => { setShowForm(false); setFiles([]); setNotes(""); }} className="btn-ghost text-sm">
 Cancel
 </button>
 </div>
 </form>
 )}

 {/* Timeline */}
 {entries.length === 0 ? (
 <EmptyState
 icon={Camera}
 title={isFree ? "Photo Trail, for paid plans" : "Your first month of looking-after starts here"}
 description={
 isFree
 ? "Photo Trail keeps a friendly monthly log of your home, so if anyone ever twists the story about how you cared for the place, you&rsquo;ve got the truth on file. Available on Vault, Compass and Pro."
 : "Take 5 minutes to walk around and snap a few rooms. We&rsquo;ll quietly date and look after every photo. So if anything ever goes sideways, you&rsquo;ve got peace of mind."
 }
 actionLabel={isFree ? "See plans" : "Start your first check-in"}
 actionTo={isFree ? "/pricing" : undefined}
 onAction={isFree ? undefined : () => setShowForm(true)}
 testid="photo-trail-empty"
 secondary={isFree ? undefined : "We&rsquo;ll send a friendly nudge once a month, skip if everything&rsquo;s fine."}
 />
 ) : (
 <div className="space-y-4">
 {entries.map(e => (
 <div key={e.id} className={`card border-l-4 ${e.is_exit ? "border-l-[#B35D4C]" : "border-l-[#1B6C62]"}`} data-testid={`photo-trail-entry-${e.id}`}>
 <div className="flex items-start justify-between gap-3 mb-3">
 <div>
 <div className="flex items-center gap-2 flex-wrap">
 <span className="font-serif text-lg text-[#0D2B52]">{e.room_category}</span>
 {e.is_exit && <span className="px-2 py-0.5 text-[10px] font-mono bg-[#B35D4C]/10 text-[#B35D4C] uppercase">Exit check-in</span>}
 </div>
 <div className="flex items-center gap-2 text-[12px] text-[#5a6b7a] mt-0.5">
 <Calendar size={11} strokeWidth={1.5} />
 {new Date(e.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}
 <span>·</span>
 <span>{e.documents?.length || 0} photo(s)</span>
 </div>
 </div>
 <button
 onClick={() => deleteEntry(e.id)}
 className="text-[#B35D4C] hover:opacity-80 p-1"
 title="Delete this check-in"
 data-testid={`photo-trail-delete-${e.id}`}
 >
 <Trash2 size={14} strokeWidth={1.5} />
 </button>
 </div>
 {e.notes && (
 <p className="text-[13px] text-[#1A1A1A] font-serif italic mb-3 border-l-2 border-[#E5E4DE] pl-3">
 {e.notes}
 </p>
 )}
 {/* Photo grid with per-photo hash */}
 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
 {(e.documents || []).map(d => (
 <div key={d.id} className="border border-[#E5E4DE] bg-white" data-testid={`photo-trail-photo-${d.id}`}>
 <a href={`/api/documents/${d.id}/download?inline=1`} target="_blank" rel="noopener noreferrer" className="block aspect-[4/3] bg-[#EFEDE5] overflow-hidden">
 <img
 src={`/api/documents/${d.id}/download?inline=1`}
 alt={d.original_name}
 className="w-full h-full object-cover"
 loading="lazy"
 />
 </a>
 <div className="px-2 py-1.5 border-t border-[#E5E4DE]">
 <div className="flex items-center gap-1 text-[10px] font-mono text-[#5a6b7a] truncate" title={d.sha256}>
 <Fingerprint size={9} strokeWidth={1.75} />
 <span className="truncate">{d.sha256 ? d.sha256.slice(0, 16) : ","}</span>
 </div>
 {d.sha256 && (
 <div className="flex items-center gap-1.5 mt-1">
 <button
 onClick={() => copyHash(d.sha256)}
 className="text-[10px] text-[#1B6C62] hover:opacity-80 inline-flex items-center gap-0.5"
 data-testid={`photo-trail-copy-hash-${d.id}`}
 >
 <Copy size={9} /> Copy
 </button>
 <a
 href={verifyUrl(d.sha256)}
 target="_blank"
 rel="noopener noreferrer"
 className="text-[10px] text-[#1B6C62] hover:opacity-80 inline-flex items-center gap-0.5"
 >
 <ShieldCheck size={9} /> Verify
 </a>
 </div>
 )}
 </div>
 </div>
 ))}
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 );
}
